import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { useCustomerQuery } from "@/v2-backoffice-client/customer/customer.query"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useUserTenantQuery } from "@/v2-backoffice-client/user/tenant/user.tenant.query"
import UserCustomerUpdateSelectorItem from "@/v2-backoffice-client/user/customer/update/UserCustomerUpdateSelectorItem"

export type UserCustomerUpdateSelectorListProps = {
  user: UserFragment
  /** Whether the list is showing; the reads wait for it. */
  open: boolean
  onUpdated?(): void
}

/**
 * The search and list of a user's owner picker, shared by the popover and
 * the dialog. Pinned above the search results: the owner today (checked),
 * the customers of the user's teams (the expected picks, one click each),
 * and, when HubSpot merged any of those customers' companies away, the
 * surviving customers they point at (the pick the leftover rows refuse in
 * favour of). Everything else is reached by searching, and takes a
 * confirmation when it owns none of the user's teams.
 */
function UserCustomerUpdateSelectorList(
  props: UserCustomerUpdateSelectorListProps,
) {
  const { user, open, onUpdated } = props

  const [search, setSearch] = useState("")
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const ownerId = selectedId ?? user.CustomerId ?? null

  // The user's live teams, for the customers to suggest.
  const { data: tenantData } = useUserTenantQuery({
    skip: !open,
    variables: { userId: user.Id, onlyDeleted: false },
  })
  const teamCustomerExternalIds = [
    ...new Set(
      (tenantData?.getUserTenantsByUserId ?? [])
        .map((userTenant) => userTenant.Tenant?.ExternalCustomerId)
        .filter((id): id is string => Boolean(id)),
    ),
  ]
  const pinnedExternalIds = [
    ...new Set(
      [user.Customer?.ExternalCustomerId, ...teamCustomerExternalIds].filter(
        (id): id is string => Boolean(id),
      ),
    ),
  ]

  const { data: pinnedData, loading: pinnedLoading } = useCustomerQuery({
    skip: !open || pinnedExternalIds.length === 0,
    variables: {
      input: {
        limit: pinnedExternalIds.length,
        filters: [{ column: "ExternalCustomerId", in: pinnedExternalIds }],
      },
    },
  })
  const ownCustomers = pinnedData?.getCustomers.items ?? []

  // The survivors of the pinned customers HubSpot merged away: known only
  // once the pinned rows are in (Customer.MergedInto), and not among them
  // unless a team already sits on the survivor.
  const pinnedExternalIdSet = new Set(pinnedExternalIds)
  const survivorExternalIds = [
    ...new Set(
      ownCustomers
        .map(
          (customer) =>
            customer.MergedInto?.SurvivingCustomer?.ExternalCustomerId,
        )
        .filter(
          (id): id is string =>
            typeof id === "string" && id !== "" && !pinnedExternalIdSet.has(id),
        ),
    ),
  ]
  const { data: survivorData, loading: survivorLoading } = useCustomerQuery({
    skip: !open || survivorExternalIds.length === 0,
    variables: {
      input: {
        limit: survivorExternalIds.length,
        filters: [{ column: "ExternalCustomerId", in: survivorExternalIds }],
      },
    },
  })

  const { data, loading, error } = useCustomerQuery({
    skip: !open || search.length < 2,
    variables: {
      input: {
        limit: 10,
        filters:
          search.length >= 2
            ? [{ column: "Name", contains: search }]
            : undefined,
      },
    },
  })

  errorToast(error, { title: "Failed to query for customers" })

  const pinnedCustomers = [
    ...ownCustomers,
    ...(survivorData?.getCustomers.items ?? []),
  ]
  const pinnedIds = new Set(pinnedCustomers.map((customer) => customer.Id))
  const results = (data?.getCustomers.items ?? []).filter(
    (customer) => !pinnedIds.has(customer.Id),
  )

  // The leftovers among the user's customers that HubSpot merged into a
  // given candidate: what makes that candidate the expected pick.
  function mergedFromOf(candidate: CustomerFragment) {
    return ownCustomers.filter(
      (customer) =>
        customer.Id !== candidate.Id &&
        customer.MergedInto?.SurvivingCustomerId === candidate.Id,
    )
  }

  return (
    <>
      <Selector.Search
        placeholder="Search by customer name..."
        onSearch={setSearch}
      />

      <Selector.List
        emptyLabel="No customers found"
        isLoading={loading || pinnedLoading || survivorLoading}
        error={error}
        noResults={results.length === 0}
        pinned={pinnedCustomers.map((customer) => (
          <UserCustomerUpdateSelectorItem
            key={customer.Id}
            customer={customer}
            user={user}
            mergedFrom={mergedFromOf(customer)}
            added={customer.Id === ownerId}
            onSelect={() => {
              setSelectedId(customer.Id)
              onUpdated?.()
            }}
          />
        ))}
      >
        {results.map((customer) => (
          <UserCustomerUpdateSelectorItem
            key={customer.Id}
            customer={customer}
            user={user}
            mergedFrom={mergedFromOf(customer)}
            added={customer.Id === ownerId}
            onSelect={() => {
              setSelectedId(customer.Id)
              onUpdated?.()
            }}
          />
        ))}
      </Selector.List>
    </>
  )
}

export default UserCustomerUpdateSelectorList
